import * as Sentry from '@sentry/browser';
import React from 'react';
import styled from '@emotion/styled';
import { useParams } from 'react-router-dom';
import {
    HeadingText50,
    BodyTextMedium,
    Icon,
    PageHeaderSkeleton,
    SectionHeaderSkeleton,
} from '@modernhealth/vital-components';
import { colors, layout, mediaQuery } from '@modernhealth/vital-foundation';
import { useGetCalendarLinksQuery } from '@modernhealth/circles-graphql/v2';
import { useTranslation } from 'react-i18next';

import { captureException } from 'src/shared/helpers/captureException';

import { ReactComponent as Confirmation } from '../assets/confirmation.svg';
import { ReactComponent as SocialGoogle } from '../assets/social-google.svg';
import { ReactComponent as SocialOutlook } from '../assets/social-outlook.svg';
import { CircleNotFound } from '../components/CircleNotFound';
import { SessionCardRowSkeleton } from '../components/SessionCardSkeletons';

const PageWrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    maxWidth: layout.containerWidth,
    margin: '0 auto',
    alignItems: 'center',
    paddingTop: 'max(80px, 20vh)',
});

const StyledCalendarLinkContainer = styled.a({
    maxWidth: '400px',
    display: 'inline-flex',
    border: `2px solid ${colors.gray05}`,
    borderRadius: '12.5px',
    padding: '16px 26px',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    '& svg': {
        maxWidth: 16,
        maxHeight: 16,
    },
});

const LinksContainer = styled.div({
    display: 'flex',
    gap: 16,
    marginTop: 16,
    [mediaQuery.maxWidth('md')]: {
        flexDirection: 'column',
    },
});

const InlineStyledCalendarLink: React.FC<{
    href: string;
}> = ({ href, children }) => {
    return (
        <StyledCalendarLinkContainer
            css={{ display: 'flex-inline', alignItems: 'center' }}
            href={href}
            target="_blank"
            rel="noreferrer"
        >
            {React.Children.map(children, child => {
                if (typeof child === 'string') {
                    return (
                        <BodyTextMedium
                            as="span"
                            css={{ margin: '0 12px 0 8px' }}
                            fontColor="gray55"
                        >
                            {child}
                        </BodyTextMedium>
                    );
                }

                return child;
            })}

            <Icon size={20} color="gray55" name="chevron-right" />
        </StyledCalendarLinkContainer>
    );
};

const CirclesRsvpAddToCalendarPageSkeleton = (): JSX.Element => {
    return (
        <div>
            <PageHeaderSkeleton />

            <SectionHeaderSkeleton />
            <SessionCardRowSkeleton />

            <SectionHeaderSkeleton />
            <SessionCardRowSkeleton />

            <SectionHeaderSkeleton />
            <SessionCardRowSkeleton />
        </div>
    );
};

interface CirclesRsvpAddToCalendarPageParams {
    rsvpId: string;
    topicSlug: string;
}

export const CirclesRsvpAddToCalendarPage: React.FC<unknown> = () => {
    const { t } = useTranslation('circles');
    const { rsvpId } = useParams<CirclesRsvpAddToCalendarPageParams>();

    const { data: rsvpData, loading: rsvpLoading } = useGetCalendarLinksQuery({
        variables: {
            rsvpId,
        },
    });

    if (rsvpLoading && !rsvpData) {
        return <CirclesRsvpAddToCalendarPageSkeleton />;
    }

    if (rsvpData?.circleCalendarLinksByRsvp?.__typename !== 'CircleCalendarLinks') {
        const errMsg = `An unexpected typename was found: ${rsvpData?.circleCalendarLinksByRsvp?.__typename}`;
        Sentry.captureException(new Error(errMsg));
        captureException(new Error('An unexpected typename was found'), {
            typename: rsvpData?.circleCalendarLinksByRsvp?.__typename,
        });
        return <CircleNotFound />;
    }

    const { google: googleLink, outlook: outlookLink, ics } = rsvpData.circleCalendarLinksByRsvp;

    return (
        <PageWrapper>
            <Confirmation height="15rem" width="15rem" css={{ marginBottom: '3rem' }} />
            <HeadingText50>{t('pages.add_to_calendar.title')}</HeadingText50>
            <BodyTextMedium
                css={{ maxWidth: '396px', marginBottom: '16px', textAlign: 'center' }}
                fontColor="gray55"
            >
                {t('pages.add_to_calendar.description')}
            </BodyTextMedium>
            <LinksContainer>
                {googleLink ? (
                    <InlineStyledCalendarLink href={googleLink}>
                        <SocialGoogle aria-hidden />
                        Google Calendar
                    </InlineStyledCalendarLink>
                ) : null}
                {outlookLink ? (
                    <InlineStyledCalendarLink href={outlookLink}>
                        <SocialOutlook aria-hidden />
                        Microsoft Outlook
                    </InlineStyledCalendarLink>
                ) : null}
                {ics ? (
                    <InlineStyledCalendarLink href={`https://api.joinmodernhealth.com${ics}`}>
                        <Icon color="gray55" name="calendar-date" aria-hidden />
                        {t('pages.add_to_calendar.actions.ics')}
                    </InlineStyledCalendarLink>
                ) : null}
            </LinksContainer>
        </PageWrapper>
    );
};
